import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  GradientBorderAutocomplete,
  PageSizeSelector,
} from "./shared";

interface FilterBarProps {
  // フィルター入力欄
  propertyFilter: string;
  setPropertyFilter: (value: string) => void;
  roomFilter: string;
  setRoomFilter: (value: string) => void;
  lesseeFilter: string;
  setLesseeFilter: (value: string) => void;
  propertyOptions: string[];
  roomOptions: string[];
  lesseeOptions: string[];
  // 検索・クリアボタン
  mode: 'browse' | 'search';
  dataMode: 'full' | 'paginated' | null;
  isTrashView: boolean;
  contextLoading: boolean;
  handleSearch: () => void;
  handleClearSearch: () => void;
  confirmAndNavigate: (action: () => void) => void;
  // ページネーションコントロール
  aggregatedContracts: { id: number }[];
  sortOrder: 'asc' | 'desc';
  itemsPerPage: number;
  setItemsPerPage: (value: number) => void;
  clientCurrentPage: number;
  setClientCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  displayCurrentPage: number;
  displayTotalPages: number;
  getPaginationDisplayText: { fullText?: string; range?: string; total?: string } | null;
  setGlobalSortOrder: (order: 'asc' | 'desc') => void;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<void>;
}

const FilterBar: React.FC<FilterBarProps> = ({
  propertyFilter,
  setPropertyFilter,
  roomFilter,
  setRoomFilter,
  lesseeFilter,
  setLesseeFilter,
  propertyOptions,
  roomOptions,
  lesseeOptions,
  mode,
  dataMode,
  isTrashView,
  contextLoading,
  handleSearch,
  handleClearSearch,
  confirmAndNavigate,
  aggregatedContracts,
  sortOrder,
  itemsPerPage,
  setItemsPerPage,
  clientCurrentPage,
  setClientCurrentPage,
  displayCurrentPage,
  displayTotalPages,
  getPaginationDisplayText,
  setGlobalSortOrder,
  hasNextPage,
  fetchNextPage,
}) => {
  return (
    <>
      <Paper
        sx={{ p: 2, mb: 2, backgroundColor: "transparent", boxShadow: "none" }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
          {dataMode === 'paginated' ? 'データベースから検索' : 'フィルター'}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
          {/* 左側: フィルタ入力欄 */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <GradientBorderAutocomplete
              options={propertyOptions}
              inputValue={propertyFilter}
              onInputChange={(event, newInputValue) => {
                confirmAndNavigate(() => setPropertyFilter(newInputValue));
              }}
              label="物件名"
              width={220}
            />

            <GradientBorderAutocomplete
              options={roomOptions}
              inputValue={roomFilter}
              onInputChange={(event, newInputValue) => {
                confirmAndNavigate(() => setRoomFilter(newInputValue));
              }}
              label="部屋名"
              width={220}
            />

            <GradientBorderAutocomplete
              options={lesseeOptions}
              inputValue={lesseeFilter}
              onInputChange={(event, newInputValue) => {
                confirmAndNavigate(() => setLesseeFilter(newInputValue));
              }}
              label="入居者名"
              width={220}
            />
          </Box>

          {/* 右側: 検索・クリアボタン（Path Bの場合のみ表示） */}
          {(mode === 'search' || dataMode === 'paginated') && !isTrashView && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={contextLoading}
                sx={{
                  minWidth: '80px',
                  height: '40px',
                }}
              >
                検索
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                disabled={contextLoading}
                sx={{
                  minWidth: '80px',
                  height: '40px',
                }}
              >
                クリア
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ページネーションコントロール */}
      {(dataMode === 'full' || dataMode === 'paginated') && !isTrashView && aggregatedContracts.length > 0 && (
        <Box
          sx={{
            ml: 2.5,
            mr: 2.5,
            mt: 2,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* 上段: ソート順、表示件数セレクター */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
              value={sortOrder}
              exclusive
              onChange={(event, newOrder) => {
                if (newOrder !== null && !contextLoading) {
                  setGlobalSortOrder(newOrder);
                  setClientCurrentPage(1);
                }
              }}
              size="small"
              sx={{ height: '40px' }}
            >
              <ToggleButton
                value="asc"
                sx={{
                  minWidth: '70px',
                  '&.Mui-selected': {
                    backgroundColor: 'rgb(194, 140, 255)',
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: 'rgb(174, 120, 235)',
                    },
                  },
                }}
              >
                登録順
              </ToggleButton>
              <ToggleButton
                value="desc"
                sx={{
                  minWidth: '70px',
                  '&.Mui-selected': {
                    backgroundColor: 'rgb(194, 140, 255)',
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: 'rgb(174, 120, 235)',
                    },
                  },
                }}
              >
                新着順
              </ToggleButton>
            </ToggleButtonGroup>
            <PageSizeSelector
              value={itemsPerPage}
              onChange={(newValue) => confirmAndNavigate(() => setItemsPerPage(newValue))}
              disabled={contextLoading}
            />
          </Box>

          {/* 下段: ページ送りボタンと表示情報 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {/* 左側スペーサー */}
            <Box sx={{ minWidth: 120 }} />

            {/* 中央: ページ送りボタン群 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
              size="small"
              onClick={() => {
                setClientCurrentPage(1);
              }}
              disabled={displayCurrentPage === 1 || contextLoading}
              sx={{
                minWidth: '40px',
                fontSize: '1.2rem',
              }}
            >
              ◀◀
            </Button>
            <Button
              size="small"
              onClick={() => {
                setClientCurrentPage(prev => Math.max(1, prev - 1));
              }}
              disabled={clientCurrentPage === 1 || contextLoading}
              sx={{
                minWidth: '40px',
                fontSize: '1.2rem',
              }}
            >
              ◀
            </Button>
            <Typography
              variant="body2"
              sx={{
                px: 2,
                minWidth: 100,
                textAlign: 'center',
              }}
            >
              {displayCurrentPage} / {displayTotalPages}
            </Typography>
            <Button
              size="small"
              onClick={async () => {
                // 次のページに移動
                const nextPage = clientCurrentPage + 1;

                // Path Bで、次のページのデータが不足している場合は追加取得
                if (dataMode === 'paginated' && hasNextPage) {
                  const startIndex = (nextPage - 1) * itemsPerPage;
                  // 次のページの開始位置が取得済みデータの範囲外なら追加取得
                  if (startIndex >= aggregatedContracts.length) {
                    await fetchNextPage();
                  }
                }

                // ページを進める
                setClientCurrentPage(prev => Math.min(displayTotalPages, prev + 1));
              }}
              disabled={clientCurrentPage >= displayTotalPages || contextLoading}
              sx={{
                minWidth: '40px',
                fontSize: '1.2rem',
              }}
            >
              ▶
            </Button>
            <Button
              size="small"
              onClick={() => {
                // Path Aのみサポート（Path Bではランダムアクセスを防ぐ）
                if (dataMode === 'full') {
                  setClientCurrentPage(displayTotalPages);
                }
              }}
              disabled={dataMode === 'paginated' || clientCurrentPage >= displayTotalPages || contextLoading}
              sx={{
                minWidth: '40px',
                fontSize: '1.2rem',
              }}
            >
              ▶▶
            </Button>
            </Box>

            {/* 右側: 表示情報 */}
            <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right' }}>
              {getPaginationDisplayText?.fullText ?? 'ページング中'}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FilterBar;
